import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import {
  UserPlus, Briefcase, Edit3, Loader2, X, Save, Truck, Shield, Mail, Phone,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (d) => {
  if (!d) return 'Something went wrong.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return d?.msg || JSON.stringify(d);
};

const ManageStaff = () => {
  const [drivers, setDrivers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [tab, setTab] = useState('drivers');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(null);    // 'driver' | 'employee' | null
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([
        axios.get(`${API}/drivers`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
      ]);
      setDrivers(d.data?.items || d.data || []);
      setEmployees((u.data || []).filter((x) => ['dispatcher', 'company_admin'].includes(x.role)));
    } catch (e) {
      toast.error('Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="manage-staff-page">
        <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Manage staff
            </h1>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Manually onboard drivers and dispatchers. Edit biographical information for any driver in your fleet.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAdd('driver')}
              data-testid="add-driver-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded-md font-semibold text-sm"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <UserPlus className="w-4 h-4" /> Add driver
            </button>
            <button
              onClick={() => setShowAdd('employee')}
              data-testid="add-employee-button"
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-md font-semibold text-sm"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <Briefcase className="w-4 h-4" /> Add employee
            </button>
          </div>
        </header>

        <div className="flex gap-2 mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <button onClick={() => setTab('drivers')} data-testid="tab-drivers" className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${tab === 'drivers' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-700'}`}>
            <Truck className="w-3 h-3 inline-block mr-1" /> Drivers ({drivers.length})
          </button>
          <button onClick={() => setTab('employees')} data-testid="tab-employees" className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${tab === 'employees' ? 'bg-[#002FA7] text-white' : 'bg-white border border-slate-300 text-slate-700'}`}>
            <Shield className="w-3 h-3 inline-block mr-1" /> Employees ({employees.length})
          </button>
        </div>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : tab === 'drivers' ? (
          drivers.length === 0 ? (
            <Empty msg="No drivers yet. Click 'Add driver' to onboard one manually." />
          ) : (
            <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="drivers-list">
              {drivers.map((d) => (
                <div key={d.id || d._id} data-testid={`driver-row-${d.id || d._id}`} className="p-4 flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-900 truncate">{d.name}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                      <span><Mail className="w-3 h-3 inline-block mr-0.5" /> {d.email || '—'}</span>
                      <span><Phone className="w-3 h-3 inline-block mr-0.5" /> {d.phone || '—'}</span>
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-bold uppercase">{d.status || 'registered'}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => setEditId(d.id || d._id)}
                    data-testid={`driver-edit-${d.id || d._id}`}
                    className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 hover:bg-slate-50 text-slate-700 rounded text-xs font-semibold"
                  >
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                </div>
              ))}
            </div>
          )
        ) : employees.length === 0 ? (
          <Empty msg="No dispatchers or admins yet. Click 'Add employee'." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="employees-list">
            {employees.map((u) => (
              <div key={u.id || u._id} data-testid={`employee-row-${u.id || u._id}`} className="p-4 flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-900 truncate">{u.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    <span><Mail className="w-3 h-3 inline-block mr-0.5" /> {u.email}</span>
                    <span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px] font-bold uppercase">{u.role}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && <AddModal kind={showAdd} onClose={() => setShowAdd(null)} onDone={() => { setShowAdd(null); load(); }} />}
      {editId && <EditDriverModal driverId={editId} onClose={() => setEditId(null)} onDone={() => { setEditId(null); load(); }} />}
    </DashboardLayout>
  );
};

const Empty = ({ msg }) => (
  <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-sm text-slate-500" data-testid="staff-empty" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
    {msg}
  </div>
);

const Field = ({ label, name, value, onChange, type = 'text', testid, required }) => (
  <div>
    <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
      {label}{required && ' *'}
    </label>
    <input
      type={type} name={name} value={value || ''} onChange={onChange}
      data-testid={testid} required={required}
      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:outline-none"
      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
    />
  </div>
);

const AddModal = ({ kind, onClose, onDone }) => {
  const [form, setForm] = useState({
    name: '', email: '', password: '', phone: '',
    role: 'dispatcher', driver_type: 'company_driver',
  });
  const [busy, setBusy] = useState(false);
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const url = kind === 'driver' ? `${API}/admin/drivers` : `${API}/admin/employees`;
      await axios.post(url, form, { withCredentials: true });
      toast.success(`${kind === 'driver' ? 'Driver' : 'Employee'} created`);
      onDone();
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  return (
    <Modal title={`Add ${kind}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid={`add-${kind}-form`}>
        <Field label="Full name" name="name" value={form.name} onChange={onChange} testid={`add-${kind}-name`} required />
        <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} testid={`add-${kind}-email`} required />
        <Field label="Phone" name="phone" value={form.phone} onChange={onChange} testid={`add-${kind}-phone`} />
        <Field label="Initial password (must meet policy)" name="password" type="password" value={form.password} onChange={onChange} testid={`add-${kind}-password`} required />
        {kind === 'driver' ? (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver type</label>
            <select name="driver_type" value={form.driver_type} onChange={onChange} data-testid="add-driver-type" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <option value="company_driver">Company driver</option>
              <option value="owner_operator">Owner operator</option>
            </select>
          </div>
        ) : (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Role</label>
            <select name="role" value={form.role} onChange={onChange} data-testid="add-employee-role" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <option value="dispatcher">Dispatcher</option>
              <option value="company_admin">Company admin</option>
            </select>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600">Cancel</button>
          <button type="submit" disabled={busy} data-testid={`add-${kind}-submit`} className="inline-flex items-center gap-1 px-4 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded text-sm font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Create
          </button>
        </div>
      </form>
    </Modal>
  );
};

const EditDriverModal = ({ driverId, onClose, onDone }) => {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/drivers/${driverId}/profile`, { withCredentials: true });
        setProfile(res.data);
      } catch (e) {
        toast.error('Failed to load driver');
        onClose();
      }
    })();
  }, [driverId, onClose]);
  const onChange = (e) => setProfile({ ...profile, [e.target.name]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...profile };
      delete payload.driver_id; delete payload.user_id; delete payload.email;
      await axios.patch(`${API}/admin/drivers/${driverId}`, payload, { withCredentials: true });
      toast.success('Driver updated');
      onDone();
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };
  if (!profile) return <Modal title="Edit driver" onClose={onClose}><div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></Modal>;
  return (
    <Modal title={`Edit ${profile.name || 'driver'}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="edit-driver-form">
        <Field label="Full name" name="name" value={profile.name} onChange={onChange} testid="edit-driver-name" />
        <p className="text-[10px] text-slate-400">Email cannot be changed: {profile.email}</p>
        <Field label="Phone" name="phone" value={profile.phone} onChange={onChange} testid="edit-driver-phone" />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Driver type</label>
            <select name="driver_type" value={profile.driver_type || 'company_driver'} onChange={onChange} data-testid="edit-driver-type" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="company_driver">Company driver</option>
              <option value="owner_operator">Owner operator</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Status</label>
            <select name="status" value={profile.status || 'registered'} onChange={onChange} data-testid="edit-driver-status" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm">
              <option value="registered">Registered</option>
              <option value="pending_approval">Pending approval</option>
              <option value="approved">Approved</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
        <Field label="Date of birth (MM/DD/YYYY)" name="date_of_birth" value={profile.date_of_birth} onChange={onChange} testid="edit-driver-dob" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="SSN last 4" name="ssn_last_4" value={profile.ssn_last_4} onChange={onChange} testid="edit-driver-ssn" />
          <Field label="CDL number" name="cdl_number" value={profile.cdl_number} onChange={onChange} testid="edit-driver-cdl-number" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CDL class" name="cdl_class" value={profile.cdl_class} onChange={onChange} testid="edit-driver-cdl-class" />
          <Field label="CDL expiration" name="cdl_expiration" value={profile.cdl_expiration} onChange={onChange} testid="edit-driver-cdl-exp" />
        </div>
        <Field label="Address" name="address" value={profile.address} onChange={onChange} testid="edit-driver-address" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Emergency contact name" name="emergency_contact_name" value={profile.emergency_contact_name} onChange={onChange} testid="edit-driver-emc-name" />
          <Field label="Emergency contact phone" name="emergency_contact_phone" value={profile.emergency_contact_phone} onChange={onChange} testid="edit-driver-emc-phone" />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Notes</label>
          <textarea name="notes" value={profile.notes || ''} onChange={onChange} data-testid="edit-driver-notes" rows={2} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-sm font-semibold text-slate-600">Cancel</button>
          <button type="submit" disabled={busy} data-testid="edit-driver-save" className="inline-flex items-center gap-1 px-4 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded text-sm font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save changes
          </button>
        </div>
      </form>
    </Modal>
  );
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="staff-modal">
    <div className="bg-white rounded-md max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{title}</h3>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="staff-modal-close"><X className="w-5 h-5" /></button>
      </div>
      {children}
    </div>
  </div>
);

export default ManageStaff;
